/**
 * Event editor — full form with TipTap description, schedule, venue, capacity,
 * fee (in rupees, stored as paise), cover image, organiser department, and
 * a quick link to registrations.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Eye, EyeOff, ImagePlus, Save, Users, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DEPT_OPTIONS } from "@/lib/departments";
import { tsToDateTimeLocal, dateTimeLocalToTs } from "@/lib/datetime";
import { TipTapEditor } from "@/components/editor/TipTapEditor";
import { MediaPicker } from "@/components/editor/MediaPicker";
import { StatusBadge } from "@/components/StatusBadge";
import { PageHeader } from "@/components/PageHeader";
import { useAutosave, formatSavedAt } from "@/hooks/useAutosave";
import { EventFormFields, formFieldsSchema, type EventFormField } from "@/components/events/EventFormFields";

export const Route = createFileRoute("/_app/events/$id")({ component: EventEditor });

interface EventDetail {
  id: number;
  slug: string;
  title: string;
  description_md: string | null;
  starts_at: number;
  ends_at: number | null;
  venue: string | null;
  capacity: number | null;
  fee_inr: number;
  cover_r2_key: string | null;
  register_open: number;
  status: "draft" | "published" | "completed" | "cancelled";
  organiser_dept: string | null;
  contact_email: string | null;
  /** JSON array of extra registration questions; null on events with none. */
  form_fields_json: string | null;
  updated_at: number;
}

/** The column is TEXT. A malformed value must not blank the whole editor. */
function readFormFields(json: string | null): EventFormField[] {
  if (!json) return [];
  try {
    const parsed = formFieldsSchema.safeParse(JSON.parse(json));
    return parsed.success ? parsed.data : [];
  } catch {
    return [];
  }
}

const schema = z.object({
  title: z.string().min(1).max(200),
  description_md: z.string().max(200_000).nullable(),
  starts_at_iso: z.string().min(1),
  ends_at_iso: z.string().optional(),
  venue: z.string().max(200).nullable(),
  capacity: z.coerce.number().int().min(1).nullable().or(z.literal("")),
  fee_inr_rupees: z.coerce.number().min(0),
  cover_r2_key: z.string().nullable(),
  register_open: z.boolean(),
  organiser_dept: z.string().max(60).nullable(),
  contact_email: z.string().email().or(z.literal("")).nullable(),
  form_fields_json: formFieldsSchema,
});
type FormValues = z.infer<typeof schema>;

const apiBase = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";
const r2Url = (k: string) => `${apiBase}/admin/media/file/${k}`;

function EventEditor() {
  const { id } = Route.useParams();
  const { can } = useAuth();
  const qc = useQueryClient();
  const [coverPickerOpen, setCoverPickerOpen] = useState(false);

  const { data: row, isLoading } = useQuery({
    queryKey: ["events", "detail", id],
    queryFn: () => api.get<EventDetail>(`/admin/events/${id}`),
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: row ? {
      title: row.title,
      description_md: row.description_md,
      starts_at_iso: tsToDateTimeLocal(row.starts_at),
      ends_at_iso: row.ends_at ? tsToDateTimeLocal(row.ends_at) : "",
      venue: row.venue,
      capacity: row.capacity ?? "",
      fee_inr_rupees: row.fee_inr / 100,
      cover_r2_key: row.cover_r2_key,
      register_open: !!row.register_open,
      organiser_dept: row.organiser_dept,
      contact_email: row.contact_email ?? "",
      form_fields_json: readFormFields(row.form_fields_json),
    } : undefined,
  });
  const { register, handleSubmit, watch, setValue, control, formState: { isDirty, errors } } = form;

  const update = useMutation({
    mutationFn: (v: Partial<FormValues>) => {
      const body: Record<string, unknown> = { ...v };
      if (v.starts_at_iso) body.starts_at = dateTimeLocalToTs(v.starts_at_iso);
      if (v.ends_at_iso !== undefined) body.ends_at = v.ends_at_iso ? dateTimeLocalToTs(v.ends_at_iso) : null;
      delete body.starts_at_iso; delete body.ends_at_iso;
      if (v.fee_inr_rupees != null) body.fee_inr = Math.round(v.fee_inr_rupees * 100);
      delete body.fee_inr_rupees;
      if (v.capacity === "" || v.capacity == null) body.capacity = null;
      return api.patch(`/admin/events/${id}`, body);
    },
    // List only — never the detail. Refetching the detail feeds the server row
    // back through useForm's `values`, which resets the whole form; anything
    // typed during the save round-trip would be silently reverted.
    onSuccess: () => qc.invalidateQueries({ queryKey: ["events", "list"] }),
  });
  const publish = useMutation({
    mutationFn: () => api.post(`/admin/events/${id}/publish`),
    onSuccess: () => { toast.success("Published"); qc.invalidateQueries({ queryKey: ["events"] }); },
  });
  const unpublish = useMutation({
    mutationFn: () => api.post(`/admin/events/${id}/unpublish`),
    onSuccess: () => { toast.success("Unpublished"); qc.invalidateQueries({ queryKey: ["events"] }); },
  });

  const formValues = watch();
  const { status: saveStatus, savedAt } = useAutosave({
    value: formValues,
    enabled: !!row && form.formState.isValid && isDirty,
    delayMs: 1500,
    onSave: (v) => update.mutateAsync(v as Partial<FormValues>),
  });

  if (isLoading || !row) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-4">
      <PageHeader
        title={row.title || "Untitled"}
        back={{ href: "/events", label: "Events" }}
        actions={
          <>
            <span className="text-xs text-muted-foreground">
              {saveStatus === "saving" && "Saving…"}
              {saveStatus === "saved" && `Saved ${formatSavedAt(savedAt)}`}
              {saveStatus === "dirty" && "Unsaved changes"}
              {saveStatus === "error" && <span className="text-destructive">Save failed</span>}
            </span>
            <Link to="/events/$id/registrations" as never params={{ id: String(row.id) }}>
              <Button variant="outline" size="sm"><Users className="h-4 w-4" /> Registrations</Button>
            </Link>
            <Button variant="outline" size="sm" disabled={!can("events", "update")} onClick={handleSubmit((v) => update.mutateAsync(v as Partial<FormValues>).then(() => toast.success("Saved")))}>
              <Save className="h-4 w-4" /> Save
            </Button>
            {row.status === "published" ? (
              <Button variant="secondary" size="sm" disabled={!can("events", "publish")} onClick={() => unpublish.mutate()}>
                <EyeOff className="h-4 w-4" /> Unpublish
              </Button>
            ) : (
              <Button size="sm" disabled={!can("events", "publish")} onClick={() => publish.mutate()}>
                <Eye className="h-4 w-4" /> Publish
              </Button>
            )}
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
          <Card>
            <CardContent className="space-y-4 pt-6">
              <Input className="h-12 border-0 px-0 text-3xl font-light tracking-tight focus-visible:ring-0" placeholder="Event title" {...register("title")} />
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="starts_at_iso">Starts at</Label>
                  <Input id="starts_at_iso" type="datetime-local" {...register("starts_at_iso")} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ends_at_iso">Ends at (optional)</Label>
                  <Input id="ends_at_iso" type="datetime-local" {...register("ends_at_iso")} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="venue">Venue</Label>
                  <Input id="venue" placeholder="KRCT Main Auditorium" {...register("venue")} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="capacity">Capacity</Label>
                  <Input id="capacity" type="number" min={1} placeholder="200" {...register("capacity")} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="fee_inr_rupees">Fee (₹)</Label>
                  <Input id="fee_inr_rupees" type="number" min={0} step="0.01" {...register("fee_inr_rupees")} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="organiser_dept">Linked department (optional)</Label>
                  <Controller
                    control={control}
                    name="organiser_dept"
                    render={({ field }) => (
                      <Select
                        value={field.value ?? "__none__"}
                        onValueChange={(v) => field.onChange(v === "__none__" ? null : v)}
                      >
                        <SelectTrigger id="organiser_dept">
                          <SelectValue placeholder="— None —" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— None —</SelectItem>
                          {DEPT_OPTIONS.map((d) => (
                            <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  <p className="text-xs text-muted-foreground">If set, this event also appears on the department's page under the Events tab.</p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="contact_email">Contact email</Label>
                  <Input id="contact_email" type="email" placeholder="event@krct.ac.in" {...register("contact_email")} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Description</CardTitle>
            </CardHeader>
            <CardContent className="px-0 pt-0 pb-0">
              <Controller
                control={control}
                name="description_md"
                render={({ field }) => (
                  <TipTapEditor value={field.value ?? ""} onChange={field.onChange} surface="event" className="border-0 shadow-none" />
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Registration form</CardTitle>
            </CardHeader>
            <CardContent>
              <EventFormFields
                control={control}
                register={register}
                setValue={setValue}
                errors={errors.form_fields_json}
                disabled={!can("events", "update")}
              />
            </CardContent>
          </Card>
        </form>

        <aside className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Current</span>
                <StatusBadge status={row.status} />
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Slug</span>
                <code className="text-xs">{row.slug}</code>
              </div>
              <div className="flex items-center justify-between rounded-md border bg-secondary/40 p-3">
                <Label htmlFor="register_open" className="cursor-pointer text-sm">Registration open</Label>
                <Controller
                  control={control}
                  name="register_open"
                  render={({ field }) => (
                    <Switch id="register_open" checked={field.value} onCheckedChange={field.onChange} />
                  )}
                />
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Updated</span>
                <span>{format(row.updated_at * 1000, "MMM d, yyyy")}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Cover image</CardTitle>
            </CardHeader>
            <CardContent>
              {watch("cover_r2_key") ? (
                <div className="space-y-2">
                  <img src={r2Url(watch("cover_r2_key")!)} alt="" className="aspect-video w-full rounded-md border object-cover" />
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => setCoverPickerOpen(true)}>
                      <ImagePlus className="h-4 w-4" /> Replace
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setValue("cover_r2_key", null, { shouldDirty: true })}>
                      <XIcon className="h-4 w-4" /> Remove
                    </Button>
                  </div>
                </div>
              ) : (
                <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => setCoverPickerOpen(true)}>
                  <ImagePlus className="h-4 w-4" /> Choose image
                </Button>
              )}
            </CardContent>
          </Card>
        </aside>
      </div>

      <MediaPicker
        open={coverPickerOpen}
        onOpenChange={setCoverPickerOpen}
        onPick={(url) => {
          const key = url.split("/admin/media/file/")[1] ?? "";
          setValue("cover_r2_key", key, { shouldDirty: true });
          setCoverPickerOpen(false);
        }}
      />
    </div>
  );
}
